import { Dispatch, SetStateAction, useEffect, useState } from 'react'
import {
  Box,
  Grid,
  FormControl,
  Select,
  MenuItem,
  TextField,
  Button,
  InputLabel,
} from '@mui/material'
import { Cached, ExpandLess, ExpandMore, Search } from '@mui/icons-material'
import { DatePicker } from '@mui/x-date-pickers'
import styled from '@emotion/styled'
import { initialFilters } from './index'
import { useQueryState } from 'use-location-state'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { TenderSearchFilters } from 'pages/tender-search/tender-search-filter'
import { isValid } from 'date-fns'
import { formattedTimeStandart } from 'hooks/date/formattedTime'
import dayjs from 'dayjs'

const StyledMenuItem = styled(MenuItem)({
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#8a94a6',
  background: '#fff',
  padding: '14px 16px',
  '&:hover': {
    background: 'rgba(3, 34, 76, 0.12)',
  },
})

const StyledButton = styled(Button)({
  display: 'inline-flex',
  alignItems: 'center',
  cursor: 'pointer',
  borderRadius: '4px',
  height: '48px',
  padding: '14px',
  textDecoration: 'none',
  transition: 'all 0.4s ease 0s',
  '& svg': {
    color: '#fff',
    fontSize: '20px',
  },
})

const StyledInputLabel = styled(InputLabel)({
  fontWeight: 500,
  fontSize: '14px',
  lineHeight: '20px',
  color: '#050f21',
  marginBottom: '4px',
})

type Props = {
  setFilters: Dispatch<SetStateAction<TenderSearchFilters>>
  defaultValues: TenderSearchFilters
}

export const CompetitionFilters = ({ setFilters, defaultValues }: Props) => {
  const { t } = useTranslation()
  const [openMoreFilters, setOpenMoreFilters] = useState(false)
  // eslint-disable-next-line @typescript-eslint/no-unused-vars
  const [_page, setPage] = useQueryState('page', 0)

  const {
    register,
    control,
    handleSubmit,
    reset,
    setValue,
    watch,
    formState: { dirtyFields },
  } = useForm({
    defaultValues,
  })

  const tenderStatus = watch('status')
  const allValues = watch()

  useEffect(() => {
    if (tenderStatus && dirtyFields.status) {
      onSubmit(allValues)
    }
  }, [tenderStatus, dirtyFields])

  const resetFilters = () => {
    setFilters(initialFilters)
    reset(initialFilters)
    setValue('startFrom', null)
    setValue('startUntil', null)
  }

  const onSubmit = (values: TenderSearchFilters) => {
    setPage(0)
    setFilters(values)
  }

  return (
    <>
      <form id="competition-filter-form" onSubmit={handleSubmit(onSubmit)}>
        <Box
          sx={{
            margin: '32px 0',
            position: 'relative',
          }}
        >
          <Grid
            container
            justifyContent="space-between"
            rowGap="10px"
            columnGap={{ xs: 0, md: '10px' }}
            sx={{
              padding: '20px',
              border: '1px solid #e1e4e8',
              borderRadius: openMoreFilters ? '4px 4px 0 0' : '4px',
            }}
          >
            <Grid item xs={12} md={'auto'}>
              <FormControl fullWidth>
                <Controller
                  control={control}
                  name="status"
                  render={({ field }) => (
                    <Select
                      {...field}
                      id="tender-status"
                      sx={{
                        background: 'rgba(3, 34, 76, 0.12)',
                        color: 'rgb(3, 34, 76)',
                        maxWidth: '300px',
                      }}
                      MenuProps={{
                        sx: {
                          '& .Mui-selected': {
                            backgroundColor: 'rgba(3, 34, 76, 0.12)',
                            color: '#000',
                          },
                          '& .Mui-focusVisible': {
                            background: '#fff !important',
                            ':hover': {
                              backgroundColor: 'rgba(3, 34, 76, 0.12) !important',
                            },
                          },
                        },
                      }}
                      IconComponent={() => (
                        <ExpandMore
                          sx={{
                            fontSize: '24px',
                            color: 'rgb(3, 34, 76)',
                            marginRight: '16px',
                          }}
                        />
                      )}
                    >
                      <StyledMenuItem value={'ONGOING'}>
                        {t('ONGOING')}
                      </StyledMenuItem>
                      <StyledMenuItem value={'CANCELLED'}>
                        {t('CANCELLED')}
                      </StyledMenuItem>
                      <StyledMenuItem value={'COMPLETED'}>
                        {t('COMPLETED')}
                      </StyledMenuItem>
                    </Select>
                  )}
                />
              </FormControl>
            </Grid>
            <Grid
              item
              xs={9}
              md={'auto'}
              sx={{
                flexGrow: '1 !important',
              }}
            >
              <TextField
                id="search"
                placeholder={t('predmet-name')}
                variant="outlined"
                fullWidth
                sx={{
                  maxHeight: '48px',
                  '& .MuiInputBase-input': {},
                  '& fieldset': {},
                }}
                {...register('name')}
              />
            </Grid>
            <Grid item xs={12} md={'auto'} order={{ xs: 1, md: 0 }}>
              <Box
                sx={{
                  width: '100%',
                  border: '1px solid rgb(3, 34, 76)',
                  borderRadius: '4px',
                  padding: '14px 16px',
                  display: 'inline-flex',
                  justifyContent: 'space-between',
                  alignItems: 'center',
                  cursor: 'pointer',
                  transition: '.4s',
                  fontSize: '14px',
                  height: '48px',
                  fontWeight: 500,
                  color: 'rgb(3, 34, 76)',
                }}
                onClick={() => setOpenMoreFilters(!openMoreFilters)}
              >
                {t('search-more')}
                <ExpandMore />
              </Box>
            </Grid>
            <Grid item xs={3} md={'auto'}>
              <Box display="flex" justifyContent="flex-end" gap="10px">
                <StyledButton
                  sx={{
                    background: 'rgb(122, 127, 126)',
                    '&:hover': {
                      background: 'rgb(135, 141, 140)',
                    },
                  }}
                  type="submit"
                  form="compitition-filter-form"
                  onClick={handleSubmit(onSubmit)}
                >
                  <Search />
                </StyledButton>
                <StyledButton
                  sx={{
                    background: 'rgb(3, 34, 76)',
                    color: '#fff',
                    '&:hover': {
                      background: 'rgba(3, 34, 76, 0.6)',
                    },
                  }}
                  onClick={resetFilters}
                >
                  <Cached />
                </StyledButton>
              </Box>
            </Grid>
          </Grid>
          {openMoreFilters && (
            <Box
              sx={{
                position: 'absolute',
                top: '100%',
                width: '100%',
                padding: '20px',
                border: '1px solid #e1e4e8',
                borderTop: 'none',
                zIndex: '99',
                background: '#fff',
              }}
            >
              <Grid
                container
                columnSpacing="20px"
                rowSpacing="25px"
                sx={{ marginBottom: '20px' }}
              >
                <Grid item xs={12} md={6}>
                  <StyledInputLabel id="name">
                    {t('tender-organization-name')}
                  </StyledInputLabel>
                  <FormControl fullWidth>
                    <TextField
                      id="name"
                      InputLabelProps={{ shrink: false }}
                      placeholder={t('tender-organization-name')}
                      variant="outlined"
                      fullWidth
                      sx={{
                        maxHeight: '48px',
                      }}
                      {...register('orgName')}
                    />
                  </FormControl>
                </Grid>
                <Grid item xs={12} md={6}>
                  <StyledInputLabel id="competetion-number">
                    {t('createdDate')}
                  </StyledInputLabel>
                  <Box display="flex" gap="10px">
                    <FormControl sx={{ width: '50%' }}>
                      <Controller
                        name="startFrom"
                        control={control}
                        render={({
                          field: { ref, onBlur, name, ...field },
                          fieldState,
                        }) => (
                          <DatePicker
                            {...field}
                            inputRef={ref}
                            value={field.value ? field.value : null}
                            onChange={(date) => {
                              if (
                                date !== null &&
                                isValid(new Date(formattedTimeStandart(date)))
                              ) {
                                const result = dayjs(date).toISOString()
                                field.onChange(result)
                              }
                            }}
                            slotProps={{
                              textField: {
                                placeholder: t('from'),
                                onBlur,
                                name,
                                error: !!fieldState?.error,
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                    <FormControl sx={{ width: '50%' }}>
                      <Controller
                        name="startUntil"
                        control={control}
                        render={({
                          field: { ref, onBlur, name, ...field },
                          fieldState,
                        }) => (
                          <DatePicker
                            {...field}
                            inputRef={ref}
                            value={field.value ? field.value : null}
                            onChange={(date) => {
                              if (
                                date !== null &&
                                isValid(new Date(formattedTimeStandart(date)))
                              ) {
                                const result = dayjs(date).toISOString()
                                field.onChange(result)
                              }
                            }}
                            slotProps={{
                              textField: {
                                placeholder: t('to'),
                                onBlur,
                                name,
                                error: !!fieldState?.error,
                              },
                            }}
                          />
                        )}
                      />
                    </FormControl>
                  </Box>
                </Grid>
                {/*  */}
              </Grid>
              <Box
                display="flex"
                alignItems="center"
                flexWrap="wrap"
                rowGap="10px"
                sx={{
                  justifyContent: { xs: 'center', sm: 'space-between' },
                }}
              >
                <Box
                  display="flex"
                  alignItems="center"
                  justifyContent="ceter"
                  color="primary.main"
                  sx={{
                    cursor: 'pointer',
                  }}
                  onClick={() => setOpenMoreFilters(false)}
                >
                  {t('close')} <ExpandLess sx={{ marginLeft: '5px' }} />
                </Box>
                <Box sx={{ width: { xs: '100%', sm: 'auto' } }}>
                  <Button
                    onClick={resetFilters}
                    variant="outlined"
                    sx={{
                      marginRight: { xs: '15px' },
                      marginBottom: { xs: '15px', sm: 0 },
                      width: { xs: '100%', sm: 'auto' },
                    }}
                  >
                    {t('reset-search')}
                  </Button>
                  <Button
                    variant="contained"
                    style={{ color: '#fff', backgroundColor: 'rgb(3, 34, 76)' }}
                    sx={{ width: { xs: '100%', sm: 'auto' } }}
                    type="submit"
                    form="compitition-filter-form"
                    onClick={handleSubmit(onSubmit)}
                  >
                    {t('show')}
                  </Button>
                </Box>
              </Box>
            </Box>
          )}
        </Box>
      </form>
    </>
  )
}
